import { useState } from 'react'
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    CardActions,
    Grid,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Switch,
    FormControlLabel,
    Alert,
    Skeleton,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from '@mui/material'
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    Science as ScienceIcon,
} from '@mui/icons-material'
import type { LensDefinition, LensFieldMapping, LensTemplateId } from '@yourownmetric/shared'

import { useAppSelector } from '@/app/hooks'
import { EmptyState } from '@/components/shared/EmptyState'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import {
    useListLensesQuery,
    useCreateLensMutation,
    useUpdateLensMutation,
    useDeleteLensMutation,
    useListLensTemplatesQuery,
} from '@/apis/lensesApi'

// --- Template Picker Dialog ---

function TemplatePicker({
    open,
    onClose,
    onSelect,
}: {
    open: boolean
    onClose: () => void
    onSelect: (templateId: string, name: string) => void
}) {
    const { data: templates = [], isLoading } = useListLensTemplatesQuery()

    const TEMPLATE_ICONS: Record<string, string> = {
        shopping_cart: '🛒',
        cloud: '☁️',
        account_balance: '🏦',
        psychology: '🧠',
        phone_iphone: '📱',
        store: '🏪',
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Choose Industry Template</DialogTitle>
            <DialogContent>
                {isLoading ? (
                    <Grid container spacing={2}>
                        {[1, 2, 3].map(i => (
                            <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                                <Skeleton variant="rectangular" height={140} />
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        {templates.map(t => (
                            <Grid key={t.id} size={{ xs: 12, sm: 6, md: 4 }}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        cursor: 'pointer',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                            bgcolor: 'action.hover',
                                        },
                                        transition: 'all 0.15s',
                                    }}
                                    onClick={() => onSelect(t.id, t.name)}
                                >
                                    <CardContent>
                                        <Typography variant="h4" sx={{ mb: 1 }}>
                                            {TEMPLATE_ICONS[t.icon] ?? '📊'}
                                        </Typography>
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            {t.name}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mb: 1.5, minHeight: 40 }}
                                        >
                                            {t.description}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <Chip
                                                size="small"
                                                label={`${t.dimensionCount} dimensions`}
                                                variant="outlined"
                                            />
                                            <Chip
                                                size="small"
                                                label={`${t.metricCount} metrics`}
                                                variant="outlined"
                                            />
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}

                        {/* Custom option */}
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Card
                                variant="outlined"
                                sx={{
                                    cursor: 'pointer',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        bgcolor: 'action.hover',
                                    },
                                    transition: 'all 0.15s',
                                    borderStyle: 'dashed',
                                }}
                                onClick={() => onSelect('', 'Custom Lens')}
                            >
                                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                                    <AddIcon
                                        sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }}
                                    />
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        Custom
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Define your own dimensions & metrics
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
            </DialogActions>
        </Dialog>
    )
}

// --- Field Mapping Table ---

function FieldMappingsTable({
    title,
    fields,
    type,
}: {
    title: string
    fields: LensFieldMapping[]
    type: 'dimension' | 'metric'
}) {
    if (fields.length === 0) {
        return null
    }

    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {title}
            </Typography>
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Property Key</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Slot</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {fields.map((f, i) => (
                            <TableRow key={i}>
                                <TableCell>
                                    <Tooltip title={f.description ?? ''}>
                                        <Typography variant="body2">{f.name}</Typography>
                                    </Tooltip>
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        variant="body2"
                                        fontFamily="monospace"
                                        fontSize={12}
                                    >
                                        {f.propertyKey}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        label={type === 'dimension' ? 'string' : 'number'}
                                        color={type === 'dimension' ? 'info' : 'success'}
                                        variant="outlined"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                        {type === 'dimension'
                                            ? `blob${f.slotIndex + 12}`
                                            : `double${f.slotIndex + 4}`}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    )
}

// --- View Lens Detail Dialog ---

function LensDetailDialog({
    open,
    lens,
    onClose,
}: {
    open: boolean
    lens: LensDefinition | null
    onClose: () => void
}) {
    if (!lens) {
        return null
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {lens.name}
                <Chip
                    size="small"
                    label={lens.active ? 'Active' : 'Inactive'}
                    color={lens.active ? 'success' : 'default'}
                    sx={{ ml: 1.5 }}
                />
            </DialogTitle>
            <DialogContent>
                {lens.templateId && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Based on the <strong>{lens.templateId}</strong> industry template
                    </Alert>
                )}
                <FieldMappingsTable
                    title={`Dimensions (${lens.dimensions.length}/9)`}
                    fields={lens.dimensions}
                    type="dimension"
                />
                <FieldMappingsTable
                    title={`Metrics (${lens.metrics.length}/17)`}
                    fields={lens.metrics}
                    type="metric"
                />
                <Typography variant="caption" color="text.secondary">
                    Created: {new Date(lens.createdAt).toLocaleDateString()}
                    {' · '}
                    Updated: {new Date(lens.updatedAt).toLocaleDateString()}
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    )
}

// --- Edit Lens Name Dialog ---

function EditNameDialog({
    open,
    currentName,
    onClose,
    onSave,
}: {
    open: boolean
    currentName: string
    onClose: () => void
    onSave: (name: string) => void
}) {
    const [name, setName] = useState(currentName)

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Edit Lens Name</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    fullWidth
                    label="Name"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    sx={{ mt: 1 }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    disabled={!name.trim()}
                    onClick={() => onSave(name.trim())}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    )
}

// --- Main Lenses Page ---

export function LensesPage() {
    const projectId = useAppSelector(s => s.project.selectedId)
    const { data: lenses = [], isLoading } = useListLensesQuery(
        { projectId: projectId! },
        { skip: !projectId },
    )
    const [createLens] = useCreateLensMutation()
    const [updateLens] = useUpdateLensMutation()
    const [deleteLens] = useDeleteLensMutation()

    const [showTemplates, setShowTemplates] = useState(false)
    const [detailLens, setDetailLens] = useState<LensDefinition | null>(null)
    const [editLens, setEditLens] = useState<LensDefinition | null>(null)
    const [deletingId, setDeletingId] = useState<string | null>(null)

    if (!projectId) {
        return (
            <Box sx={{ p: 3 }}>
                <EmptyState
                    icon={<ScienceIcon />}
                    title="No project selected"
                    description="Select a project to manage its custom data lenses."
                />
            </Box>
        )
    }

    const handleCreateFromTemplate = async (templateId: string, name: string) => {
        setShowTemplates(false)
        await createLens({
            projectId: projectId!,
            name,
            ...(templateId ? { templateId: templateId as LensTemplateId } : {}),
        })
    }

    const handleToggleActive = async (lens: LensDefinition) => {
        await updateLens({ id: lens.id, active: !lens.active })
    }

    const handleRename = async (name: string) => {
        if (editLens) {
            await updateLens({ id: editLens.id, name })
            setEditLens(null)
        }
    }

    const handleDelete = async () => {
        if (deletingId) {
            await deleteLens(deletingId)
            setDeletingId(null)
        }
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                <Box>
                    <Typography variant="h5">Lenses</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Custom data dimensions that map your event properties to analytics fields
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setShowTemplates(true)}
                >
                    Create Lens
                </Button>
            </Box>

            {isLoading ? (
                <Grid container spacing={2}>
                    {[1, 2, 3].map(i => (
                        <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                            <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 1 }} />
                        </Grid>
                    ))}
                </Grid>
            ) : lenses.length === 0 ? (
                <EmptyState
                    icon={<ScienceIcon />}
                    title="No lenses configured"
                    description="Create a lens to start tracking custom dimensions and metrics for your events. Pick an industry template or build your own."
                    action={
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => setShowTemplates(true)}
                        >
                            Create your first Lens
                        </Button>
                    }
                />
            ) : (
                <Grid container spacing={2}>
                    {lenses.map(lens => (
                        <Grid key={lens.id} size={{ xs: 12, sm: 6, md: 4 }}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                        }}
                                    >
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight={600}>
                                                {lens.name}
                                            </Typography>
                                            {lens.templateId && (
                                                <Chip
                                                    size="small"
                                                    label={lens.templateId}
                                                    variant="outlined"
                                                    sx={{ mt: 0.5 }}
                                                />
                                            )}
                                        </Box>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    size="small"
                                                    checked={lens.active}
                                                    onChange={() => handleToggleActive(lens)}
                                                />
                                            }
                                            label=""
                                        />
                                    </Box>
                                    <Box
                                        sx={{
                                            mt: 1.5,
                                            display: 'flex',
                                            gap: 0.5,
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        <Chip
                                            size="small"
                                            label={`${lens.dimensions.length} dimensions`}
                                            color="info"
                                            variant="outlined"
                                        />
                                        <Chip
                                            size="small"
                                            label={`${lens.metrics.length} metrics`}
                                            color="success"
                                            variant="outlined"
                                        />
                                    </Box>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ mt: 1, display: 'block' }}
                                    >
                                        Updated {new Date(lens.updatedAt).toLocaleDateString()}
                                    </Typography>
                                </CardContent>
                                <CardActions>
                                    <Tooltip title="View details">
                                        <IconButton
                                            size="small"
                                            onClick={() => setDetailLens(lens)}
                                        >
                                            <ViewIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Edit name">
                                        <IconButton size="small" onClick={() => setEditLens(lens)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Box sx={{ flex: 1 }} />
                                    <Tooltip title="Delete">
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => setDeletingId(lens.id)}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Dialogs */}
            <TemplatePicker
                open={showTemplates}
                onClose={() => setShowTemplates(false)}
                onSelect={handleCreateFromTemplate}
            />

            <LensDetailDialog
                open={Boolean(detailLens)}
                lens={detailLens}
                onClose={() => setDetailLens(null)}
            />

            {editLens && (
                <EditNameDialog
                    open={Boolean(editLens)}
                    currentName={editLens.name}
                    onClose={() => setEditLens(null)}
                    onSave={handleRename}
                />
            )}

            <ConfirmDialog
                open={Boolean(deletingId)}
                title="Delete Lens"
                message="This will permanently remove this lens and stop writing its custom dimensions. Historical data in Analytics Engine will remain for 90 days."
                onConfirm={handleDelete}
                onCancel={() => setDeletingId(null)}
            />
        </Box>
    )
}
